<script>
  import { onMount } from "svelte";
  import { DateTime } from "luxon";
  import Table from "./components/Table.svelte";
  import DeployHour from "./DeployHour.svelte";
  import ProbeStatusPlot from "./ProbeStatusPlot.svelte";
  import { medianAbsoluteDeviation, median } from "simple-statistics";

  let deployColumns = [
    {
      name: "deployed (±15m UTC)",
      format: (row) => row.submission_timestamp.substr(0, 16),
    },
    {
      name: "relative",
      format: (row) => DateTime.fromISO(row.submission_timestamp).toRelative(),
    },
    { name: "project", format: (row) => row.project },
    {
      name: "commit time (UTC)",
      format: (row) => row.commit_timestamp.substr(0, 16),
    },
    {
      name: "commit hash",
      format: (row) => `<a
          href="https://github.com/mozilla-services/mozilla-pipeline-schemas/commit/${row.commit_hash}">
          ${row.commit_hash}
        </a>`,
      html: true,
    },
  ];

  let pendingColumns = [
    {
      name: "commit time (UTC)",
      format: (row) => row.committed_timestamp.substr(0, 16),
    },
    {
      name: "relative",
      format: (row) =>
        DateTime.fromISO(
          row.committed_timestamp.substr(0, 16).replace(" ", "T")
        ).toRelative(),
    },
    {
      name: "commit hash",
      format: (row) => `<a
          href="https://github.com/mozilla-services/mozilla-pipeline-schemas/commit/${
            row.hexsha
          }">
          ${row.hexsha.slice(0, 8)}
        </a>`,
      html: true,
    },
    { name: "message", format: (row) => row.message },
  ];

  let probeinfoColumns = [
    {
      name: "last updated",
      format: (row) => row.last_update.substr(0, 16),
    },
    {
      name: "relative",
      format: (row) => DateTime.fromISO(row.last_update).toRelative(),
    },
    {
      name: "delta (hours)",
      format: (row) => row.delta,
    },
    {
      // NOTE: this is a derived field
      name: "z-score (MAD)",
      format: (row) => row.score.toFixed(1),
    },
    {
      name: "status",
      format: (row, threshold = 3) => {
        if (row.score < threshold) {
          return "nominal";
        } else {
          return `<span style="background: yellow;">partial outage</span>`;
        }
      },
      html: true,
    },
  ];

  function computeModifiedZScore(row) {
    let deltas = probeinfoStatus.map((row) => row.delta);
    let mad = medianAbsoluteDeviation(deltas);
    // see https://en.wikipedia.org/wiki/Median_absolute_deviation
    return (row.delta - median(deltas)) / (1.4826 * mad);
  }

  function getMinutes(dates) {
    return dates.map((x) => {
      let dt = DateTime.fromISO(x, { zone: "utc" });
      return dt.hour * 60 + dt.minute;
    });
  }

  let deploys;
  let pendingCommits;
  let probeinfoStatus;

  // modified by getData
  let modified;

  async function getData(table) {
    let resp = await fetch(`data/mozilla_pipeline_schemas/${table}.json`);
    let data = await resp.json();
    modified = new Date(resp.headers.get("last-modified")).toISOString();
    return data;
  }

  onMount(async () => {
    deploys = await getData("deploys");
    deploys.sort(
      (x, y) =>
        DateTime.fromISO(y.submission_timestamp) -
        DateTime.fromISO(x.submission_timestamp)
    );
    pendingCommits = await getData("pending_commits");
    probeinfoStatus = await getData("probeinfo_status");
    probeinfoStatus = probeinfoStatus.map((row) => ({
      ...row,
      score: computeModifiedZScore(row),
    }));
  });

  let refs = {
    bq_deploy:
      "https://docs.telemetry.mozilla.org/concepts/pipeline/schemas.html#deploying-to-bigquery",
    docs: "https://docs.telemetry.mozilla.org/concepts/pipeline/schemas.html",
    generated_schemas:
      "https://github.com/mozilla-services/mozilla-pipeline-schemas/commits/generated-schemas/schemas",
    source:
      "https://github.com/mozilla-services/data-sandbox-terraform/tree/master/projects/monitoring-queries",
    bug: "https://bugzilla.mozilla.org/show_bug.cgi?id=1609738",
    console:
      "https://console.cloud.google.com/home/dashboard?project=monitoring-queries",
    mad: "https://en.wikipedia.org/wiki/Median_absolute_deviation",
  };
</script>

<main>
  <h1>mozilla-pipeline-schema deploys</h1>
  <p>
    <i>
      Data last updated:
      {DateTime.fromISO(modified).toRelative()}
      ({modified})
    </i>
  </p>
  <p>
    This page shows the history of
    <a href={refs.bq_deploy}>successful schema deploys.</a>
    See the
    <a href={refs.docs}>Mozilla Data Documentation section on schemas</a>
    for more information about the deployment pipeline. See the
    <a href={refs.generated_schemas}>generated-schemas commit log </a>
    for detailed information on schema changes. See the
    <a href={refs.source}>data-sandbox-terraform </a>
    repository for the source of this site. See
    <a href={refs.bug}>bug 1609738</a>
    for context on why this site was built.
  </p>
  <p>
    If you have access to the
    <a href={refs.console}>monitoring-queries sandbox project,</a>
    you may query the table directly.
  </p>
  <pre>
    <code>
SELECT * FROM `monitoring-queries`.mozilla_pipeline_schemas.deploys
SELECT * FROM `monitoring-queries`.mozilla_pipeline_schemas.pending_commits
SELECT * FROM `monitoring-queries`.mozilla_pipeline_schemas.probeinfo_status
    </code>
  </pre>
  <div>
    {#if pendingCommits && pendingCommits.length > 0}
      <h2>Pending Commits</h2>
      <Table data={pendingCommits} columns={pendingColumns} />
    {/if}
    <h2 id="probe-scraper-status">Probe Scraper Status</h2>
    <p>
      This service scrapes metrics definitions across Telemetry and Glean. The
      schema deploy process depends on this upstream service and may not move
      forward until it succeeds.
    </p>

    {#if probeinfoStatus}
      <p>
        The probe scraper runs on business days and typically completes
        {median(getMinutes(probeinfoStatus.map((r) => r.last_update)))}
        minutes (median) after UTC midnight. The
        <a href={refs.mad}>median absolute deviation (MAD)</a>
        of time since the last update is
        {medianAbsoluteDeviation(probeinfoStatus.map((row) => row.delta))}
        hour(s). Rows that are more than 3 MAD above the median are considered
        partial outages. There have been
        {probeinfoStatus.filter((row) => row.score >= 3).length}
        /
        {probeinfoStatus.length}
        jobs with partial-outages monitored so far.
      </p>
      <Table
        data={probeinfoStatus}
        columns={probeinfoColumns}
        paginationSize={7} />
      <ProbeStatusPlot data={probeinfoStatus} />
    {/if}

    <h2 id="deploy-history">Deploy History</h2>
    <p>
      This contains timestamps of the last schema deploy to the BigQuery tables.
      These are collected by scraping for the label that is attached to the
      table definition. The label encodes the commit hash and timestamp of the
      schema artifact.
    </p>
    {#if deploys}
      <DeployHour data={deploys} />
      <Table data={deploys} columns={deployColumns} paginationSize={10} />
    {/if}
  </div>
</main>
